import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuick from "@/pages/home/components/CategoryQuick";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getHotComics, getLatestComics, formatDate } from "@/lib/comicUtils";

const features = [
  { icon: "ri-hd-line", title: "高清画质", desc: "章节图片清晰细腻，阅读体验舒适流畅。" },
  { icon: "ri-device-line", title: "多端适配", desc: "手机与电脑自适应布局，随时随地阅读。" },
  { icon: "ri-apps-2-line", title: "丰富分类", desc: "覆盖热血、校园、恋爱、科幻等多类题材。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "作品章节持续更新，第一时间追更新内容。" },
];

export default function Home() {
  useDocumentMeta({
    title: "歪歪漫画在线观看 - 高清漫画在线播放平台",
    description:
      "歪歪漫画在线观看提供丰富的漫画资源，涵盖多种分类，支持在线阅读，方便快捷，是漫画爱好者的理想选择。",
    keywords:
      "歪歪漫画在线观看,漫画在线,漫画阅读,歪歪漫画,在线漫画,漫画平台,漫画网站",
  });

  const latest = getLatestComics(12);
  const hot = getHotComics(6);

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main>
        <h1 className="sr-only">歪歪漫画在线观看</h1>
        <HeroCarousel />
        <CategoryQuick />

        <section className="mx-auto w-full max-w-6xl px-4 md:px-8 py-6">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950">最新更新</h2>
              <p className="mt-1 text-sm text-foreground-500">刚刚更新的漫画作品，追更不迷路</p>
            </div>
            <Link to="/category/全部" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap flex items-center gap-1">
              查看全部
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {latest.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-8 py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="mb-6">
                <h2 className="font-heading text-2xl text-foreground-950">热门推荐</h2>
                <p className="mt-1 text-sm text-foreground-500">根据阅读人气精选的作品</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {hot.map((comic, idx) => (
                  <Link
                    key={comic.id}
                    to={`/detail/${comic.id}`}
                    className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-medium ${
                        idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div className="relative h-[104px] w-[74px] shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} 漫画封面`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <h3 className="line-clamp-1 text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                        {comic.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {comic.genres.slice(0, 3).map((g) => (
                          <span key={g} className="rounded bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-800">
                            {g}
                          </span>
                        ))}
                      </div>
                      <p className="mt-auto line-clamp-1 pt-1.5 text-xs text-foreground-400">
                        {comic.chapters[comic.chapters.length - 1]?.name}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-xl text-foreground-950">平台特色</h2>
              <div className="mt-5 space-y-5">
                {features.map((f) => (
                  <div key={f.title} className="flex gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className={`${f.icon} text-xl`}></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-medium text-foreground-900">{f.title}</h3>
                      <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 md:px-8 pb-4 pt-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
            <h2 className="font-heading text-2xl text-foreground-950">关于歪歪漫画在线观看</h2>
            <p className="mt-3 max-w-4xl text-sm text-foreground-600 leading-relaxed">
              歪歪漫画在线观看是一个专注于高清漫画在线阅读的平台，汇聚热血、校园、恋爱、科幻、悬疑、搞笑、玄幻、都市等多元化题材作品。
              我们致力于为漫画爱好者提供便捷的搜索、灵活的筛选与流畅的阅读体验，帮助每一位读者快速找到心仪的作品，沉浸在精彩的漫画世界中。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-500"></i> 全部作品免费在线观看</span>
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-500"></i> 支持多条件组合筛选</span>
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-500"></i> 本地收藏方便追更</span>
              <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-500"></i> 手机端友好阅读</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/全部"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-compass-3-line"></i>
                开始探索
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
              >
                了解我们
              </Link>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-6xl px-4 md:px-8 pb-2 pt-6 text-center">
          <p className="text-xs text-foreground-400">
            本站所有内容均为虚构演示，仅用于产品功能展示，更新时间示例：{formatDate("2026-10-15")}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}